import { useState, useEffect, useMemo } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import { AppDispatch, RootState } from "@/store"
import {
    fetchWorkflows,
    deleteWorkflow,
    selectWorkflows,
    clearError,
} from "../../store/workflowSlice"
import {
    fetchLiveWorkflows,
    selectLiveWorkflows,
} from "../../store/liveWorkflowSlice"
import type { WorkflowDto } from "../../dtos/workflow.dto"
import { toast } from "sonner"
import { Button } from "@/modules/shared/ui/components/button"
import { Badge } from "@/modules/shared/ui/components/badge"
import { SearchBar } from "@/modules/shared/ui/components/custom/SearchBar"
import {
    DataTable,
    type Action as TableAction,
    type TableColumn,
} from "@/modules/shared/ui/components/custom/CustomTable"
import { StartWorkflowDialog } from "../components/StartWorkflowDialog"
import { WorkflowConfirmationDialog } from "../components/WorkflowConfirmationDialog"

export default function WorkflowManagerPage() {
    const navigate = useNavigate()
    const dispatch = useDispatch<AppDispatch>()
    const workflows = useSelector(selectWorkflows)
    const liveWorkflows = useSelector(selectLiveWorkflows)
    const { isLoading, error } = useSelector(
        (state: RootState) => state.workflows,
    )

    const [search, setSearch] = useState("")
    const [startWorkflowId, setStartWorkflowId] = useState<string | null>(null)
    const [deleteDialog, setDeleteDialog] = useState<{
        open: boolean
        workflowId?: string
        workflowName?: string
    }>({
        open: false,
    })
    const [isDeleting, setIsDeleting] = useState(false)

    useEffect(() => {
        dispatch(fetchWorkflows())
        dispatch(fetchLiveWorkflows({ includeTasks: false }))
    }, [dispatch])

    // Count live instances per workflow (only InProgress)
    const liveInstanceCounts = useMemo(() => {
        const counts: Record<string, number> = {}
        liveWorkflows
            .filter((instance) => instance.status === "InProgress")
            .forEach((instance) => {
                counts[instance.workflowId] = (counts[instance.workflowId] || 0) + 1
            })
        return counts
    }, [liveWorkflows])

    const filtered = useMemo(() => {
        const term = search.toLowerCase()
        return workflows.filter((w: WorkflowDto) =>
            w.name?.toLowerCase().includes(term),
        )
    }, [workflows, search])

    const openCreate = () => {
        navigate("/onboarding/workflows/new")
    }

    const openEdit = (id: string) => {
        navigate(`/onboarding/workflows/${id}/edit`)
    }

    const handleDelete = async (id: string, name: string) => {
        setDeleteDialog({
            open: true,
            workflowId: id,
            workflowName: name,
        })
    }

    const handleConfirmDelete = async () => {
        if (!deleteDialog.workflowId) return

        setIsDeleting(true)
        try {
            await dispatch(deleteWorkflow(deleteDialog.workflowId)).unwrap()
            toast.success("Workflow deleted")
            setDeleteDialog({ open: false })
            await dispatch(fetchWorkflows()).unwrap()
        } catch (err) {
            toast.error((err as string) || "Failed to delete workflow")
        } finally {
            setIsDeleting(false)
        }
    }

    const handleStartWorkflow = (id: string) => {
        setStartWorkflowId(id)
    }

    const columns: TableColumn<(typeof workflows)[number]>[] = [
        {
            key: "name",
            label: "Name",
            render: (wf) => wf.name,
        },
        {
            key: "isActive",
            label: "Status",
            render: (wf) => {
                const hasLiveInstances = (liveInstanceCounts[wf.id] || 0) > 0
                return (
                    <Badge
                        variant={hasLiveInstances ? "default" : "secondary"}
                        className={
                            hasLiveInstances
                                ? "bg-green-100 text-green-700"
                                : "bg-muted text-foreground"
                        }
                    >
                        {hasLiveInstances ? "Active" : "Inactive"}
                    </Badge>
                )
            },
        },
        {
            key: "updatedAt",
            label: "Updated",
            render: (wf) =>
                wf.updatedAt ? new Date(wf.updatedAt).toLocaleDateString() : "-",
        },
        {
            key: "tasks",
            label: "Tasks",
            render: (wf) => wf.tasks?.length ?? 0,
        },
    ]

    const actions: TableAction<(typeof workflows)[number]>[] = [
        {
            label: "Edit",
            onClick: (wf) => openEdit(wf.id),
        },
        {
            label: "Start Workflow",
            onClick: (wf) => handleStartWorkflow(wf.id),
        },
        {
            label: <span className="text-destructive">Delete</span>,
            onClick: (wf) => handleDelete(wf.id, wf.name),
        },
    ]

    return (
        <div className="p-4 space-y-6">
            <div className="bg-card rounded-lg border border-border p-8 shadow-sm">
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h1 className="text-3xl font-bold text-foreground">
                            Workflow Manager
                        </h1>
                        <p className="text-sm text-muted-foreground">
                            Manage onboarding workflow templates for this organisation.
                        </p>
                    </div>
                    <Button
                        onClick={openCreate}
                        disabled={isLoading}
                        className="bg-primary hover:bg-primary/90 text-primary-foreground"
                    >
                        New Workflow
                    </Button>
                </div>

                <div className="flex items-center gap-4 flex-wrap">
                    <SearchBar
                        placeholder="Search by name"
                        value={search}
                        onChange={setSearch}
                    />
                    <Badge variant="default" className="px-4 py-2 text-sm">
                        {filtered.length} Workflows
                    </Badge>
                    {error && (
                        <div className="text-sm text-destructive flex items-center gap-2">
                            <span>{error}</span>
                            <button
                                className="underline"
                                onClick={() => dispatch(clearError())}
                            >
                                clear
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <DataTable
                columns={columns}
                data={filtered}
                actions={actions}
                emptyMessage={isLoading ? "Loading..." : "No workflows found"}
            />

            <StartWorkflowDialog
                workflow={workflows.find((w) => w.id === startWorkflowId) || null}
                open={!!startWorkflowId}
                onClose={() => setStartWorkflowId(null)}
                onStarted={() => {
                    setStartWorkflowId(null)
                }}
            />

            <WorkflowConfirmationDialog
                open={deleteDialog.open}
                onOpenChange={(open) => {
                    if (!open) {
                        setDeleteDialog({ open: false })
                    }
                }}
                onConfirm={handleConfirmDelete}
                title="Delete Workflow"
                description={`Are you sure you want to delete "${deleteDialog.workflowName}"? This will permanently delete the workflow template and cannot be undone.`}
                confirmText="Delete"
                cancelText="Cancel"
                isSubmitting={isDeleting}
                variant="danger"
            />
        </div>
    )
}
